import { VerticalTimeline } from 'react-vertical-timeline-component';

import { gekoppeldeInzet, Station } from '@/components/detailPanel/types/types';
import {
  findFirstFutureStationIndex,
  getSetHeightTimeline,
  getStationTime,
} from '@/components/detailPanel/utils/utils';

import { TimelineStationElement } from './TimeLineStationElement';

import 'react-vertical-timeline-component/style.min.css';
import styles from '../DetailPanel.module.css';

export default function TrainTimeline({
  inzetId,
  stations,
  locatie,
  zitPlaatsen,
  comfortabelNorm,
  acceptabelNorm,
  volNorm,
  gekoppeldeInzetten,
  showDetails = true,
  showAllStops,
  inzetIsManco,
}: Readonly<{
  inzetId?: string;
  stations: Station[];
  locatie?: string;
  zitPlaatsen?: number;
  comfortabelNorm?: number;
  acceptabelNorm?: number;
  volNorm?: number;
  gekoppeldeInzetten?: gekoppeldeInzet[];
  toonToggle?: boolean;
  showDetails?: boolean;
  showAllStops: boolean;
  inzetIsManco?: boolean;
}>) {
  // Filter stations based on switch. Always show the first and last station of the route,
  // even if they are not a materieelKnoop (e.g. a doorkomst), so the route always has a start and end point.
  const filteredStations = showAllStops
    ? stations
    : stations.filter((station, idx) => station.materieelKnoop || idx === 0 || idx === stations.length - 1);

  const referenceDate = locatie ? new Date(locatie) : null;
  let pastStations: Station[] = [];
  let futureStations: Station[] = [];

  if (referenceDate) {
    const firstFutureIdx = findFirstFutureStationIndex(filteredStations, referenceDate, getStationTime);
    [pastStations, futureStations] =
      firstFutureIdx === -1
        ? [filteredStations, []]
        : [filteredStations.slice(0, firstFutureIdx), filteredStations.slice(firstFutureIdx)];
  } else {
    // No locatie: all stations are future
    pastStations = [];
    futureStations = filteredStations;
  }

  const { applySetHeightToPast, applySetHeightToFuture } = getSetHeightTimeline(
    pastStations.length,
    futureStations.length,
  );

  return (
    <div style={{ position: 'relative' }}>
      {pastStations.length > 0 && (
        <VerticalTimeline
          layout='1-column-left'
          className={`${styles.pastTime} ${applySetHeightToPast ? styles.setHeight : ''}`}
        >
          {pastStations.map((station, idx) => (
            <TimelineStationElement
              inzetId={inzetId}
              inzetIsManco={inzetIsManco}
              zitPlaatsen={zitPlaatsen}
              comfortabelNorm={comfortabelNorm}
              acceptabelNorm={acceptabelNorm}
              volNorm={volNorm}
              showDetails={showDetails}
              gekoppeldeInzetten={gekoppeldeInzetten}
              showAllStops={showAllStops}
              key={`${station.name}${idx}`}
              station={station}
              color='var(--tractie-color-background-accent-yellow-default)'
              iconBorderColor='var(--tractie-color-background-accent-yellow-default)'
            />
          ))}
        </VerticalTimeline>
      )}
      {futureStations.length > 0 && (
        <VerticalTimeline
          layout='1-column-left'
          className={`${styles.futureTime} ${applySetHeightToFuture ? styles.setHeight : ''}`}
        >
          {futureStations.map((station, idx) => (
            <TimelineStationElement
              inzetId={inzetId}
              inzetIsManco={inzetIsManco}
              zitPlaatsen={zitPlaatsen}
              comfortabelNorm={comfortabelNorm}
              acceptabelNorm={acceptabelNorm}
              volNorm={volNorm}
              showDetails={showDetails}
              gekoppeldeInzetten={gekoppeldeInzetten}
              showAllStops={showAllStops}
              key={`${station.name}${idx}`}
              station={station}
              color='var(--tractie-color-background-accent-blue-default)'
              iconBorderColor='var(--tractie-color-background-accent-blue-default)'
            />
          ))}
        </VerticalTimeline>
      )}
    </div>
  );
}
